/**
 * Performance optimization utilities
 * Provides helpers for performance monitoring and optimization
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Debounce function calls
 * Delays function execution until after a specified wait time
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function calls
 * Ensures function is called at most once per specified time period
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Hook for debouncing values
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for memoizing expensive computations
 */
export function useMemoizedValue<T>(
  factory: () => T,
  deps: React.DependencyList
): T {
  const ref = useRef<T | null>(null);
  const depsRef = useRef<React.DependencyList>([]);

  if (
    ref.current === null ||
    !deps.every((dep, i) => Object.is(dep, depsRef.current[i]))
  ) {
    ref.current = factory();
    depsRef.current = deps;
  }

  return ref.current;
}

/**
 * Hook for lazy initialization
 */
export function useLazyInit<T>(init: () => T): T {
  const ref = useRef<T | null>(null);

  if (ref.current === null) {
    ref.current = init();
  }

  return ref.current;
}

/**
 * Measure component render time
 */
export function useRenderTimer(componentName: string): void {
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current++;
    const start = performance.now();

    return () => {
      const duration = performance.now() - start;
      if (import.meta.env.DEV) {
        console.log(
          `[Performance] ${componentName} render #${renderCount.current}: ${duration.toFixed(2)}ms`
        );
      }
    };
  });
}

/**
 * Image lazy loading helper
 */
export function lazyLoadImage(
  src: string,
  placeholder?: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;

    if (placeholder) {
      resolve(placeholder);
    }
  });
}

/**
 * Intersection Observer hook for lazy loading
 */
export function useIntersectionObserver(
  elementRef: React.RefObject<Element>,
  options?: IntersectionObserverInit
): boolean {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, options]);

  return isIntersecting;
}

/**
 * Virtual scrolling helper - calculates visible items
 */
export function calculateVisibleItems(
  scrollTop: number,
  itemHeight: number,
  containerHeight: number,
  totalItems: number,
  overscan: number = 3
): { start: number; end: number } {
  const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const end = Math.min(totalItems, start + visibleCount + overscan * 2);

  return { start, end };
}

/**
 * Bundle size analyzer - logs component sizes in development
 */
export function logComponentSize(componentName: string, component: any): void {
  if (import.meta.env.DEV) {
    const size = JSON.stringify(component).length;
    console.log(`[Bundle Size] ${componentName}: ~${(size / 1024).toFixed(2)}KB`);
  }
}

/**
 * Performance mark utility
 */
export class PerformanceMarker {
  private marks: Map<string, number> = new Map();

  start(label: string): void {
    this.marks.set(label, performance.now());
  }

  end(label: string): number | null {
    const startTime = this.marks.get(label);
    if (!startTime) return null;

    const duration = performance.now() - startTime;
    this.marks.delete(label);

    if (import.meta.env.DEV) {
      console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
    }

    return duration;
  }

  measure(label: string, callback: () => void): number {
    this.start(label);
    callback();
    return this.end(label) || 0;
  }

  async measureAsync(label: string, callback: () => Promise<void>): Promise<number> {
    this.start(label);
    await callback();
    return this.end(label) || 0;
  }
}

/**
 * Web Vitals tracking helper
 */
export function trackWebVitals(): void {
  if (import.meta.env.PROD && 'PerformanceObserver' in window) {
    // Track Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;
      console.log('[Web Vital] LCP:', lastEntry.renderTime || lastEntry.loadTime);
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // Track First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        console.log('[Web Vital] FID:', entry.processingStart - entry.startTime);
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    // Track Cumulative Layout Shift (CLS)
    let clsScore = 0;
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsScore += entry.value;
          console.log('[Web Vital] CLS:', clsScore);
        }
      });
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });
  }
}

// Import useState
import { useState } from 'react';

// Create singleton performance marker
export const perfMarker = new PerformanceMarker();
